// Récupération automatique après un déploiement (Mantis 0028073).
//
// Symptôme : après la mise en ligne d'une nouvelle version, l'ouverture de l'application
// affiche une page blanche ; seul un CTRL+F5 (rechargement forcé sans cache) corrige.
//
// Cause : le router charge ses vues en imports dynamiques (chunks JS hashés). Quand une
// nouvelle version est déployée, l'ancien index.html / les anciens chunks référencés ne
// correspondent plus aux fichiers présents sur le serveur (ou viennent d'être supprimés du
// precache du service worker). L'import du chunk échoue et le boot reste bloqué => page blanche.
//
// Remède : dès qu'un chunk dynamique est introuvable, on force UN SEUL rechargement complet
// (garde via sessionStorage pour éviter toute boucle de rechargement). La garde est levée
// après un démarrage réussi, pour qu'un futur déploiement puisse de nouveau se récupérer seul.

const RELOAD_GUARD_KEY = "o2s-postdeploy-reload";

/** Motifs d'erreur typiques d'un chunk dynamique disparu après un déploiement. */
const CHUNK_ERROR_PATTERN =
  /dynamically imported module|importing a module script failed|failed to fetch dynamically|error loading dynamically imported module|css chunk|loading chunk/i;

/** Vrai si l'erreur fournie correspond à un échec de chargement de chunk dynamique. */
export function isChunkLoadError(error: unknown): boolean {
  const message =
    typeof error === "string"
      ? error
      : (error as { message?: string })?.message ?? String(error ?? "");
  return CHUNK_ERROR_PATTERN.test(message);
}

/**
 * Force un rechargement complet de la page, une seule fois par session de navigation.
 * Les appels suivants sont ignorés tant que la garde n'a pas été levée (cf. clearDeployReloadGuard),
 * ce qui évite toute boucle de rechargement si le problème persiste réellement.
 */
export function reloadOnceAfterDeploy(reason: string): void {
  try {
    if (sessionStorage.getItem(RELOAD_GUARD_KEY)) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
  } catch {
    // sessionStorage indisponible : on recharge quand même (sans garde anti-boucle).
  }
  console.warn(`[O2S] Rechargement automatique après déploiement (${reason}).`);
  window.location.reload();
}

/** Lève la garde de rechargement après un démarrage réussi de l'application. */
export function clearDeployReloadGuard(): void {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    /* no-op */
  }
}
